import React from 'react';
import PropTypes from 'prop-types';

import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import GrossVatTooltip from '~/components/Finances/GrossVatTooltip';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { PAYMENT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';

import { useClaim } from '../ClaimContainer';
import mixpanel from '../CmsMain/mixpanel';
import MultiPaymentsDialog from '../Finances/MultiPaymentsDialog';
import { useCms } from '../hooks/useCms';
import HoverActionField from '../HoverActionField';
import { CreditCardIcon } from '../icons';
import PencilIcon from '../icons/PencilIcon';
import TooltipLock from '../TooltipLock';

import SumContainer from './ExposuresTable/SumContainer';
import { MinimizedPaymentRequestContainer } from './PaymentRequestContainer/PaymentRequestContainer';
import ExposureTooltipedLock from './ExposureTooltipedLock';
import { getPayableWithReserveType, isExposureWriteDisabled, PENDING_COLOR } from './ExposureUtils';

function PaidSumContainer(props) {
  const { exposure, payableWithReserve } = props;

  const { claim, onClaimUpdate } = useClaim();
  const { user } = useCms();
  const payableType = getPayableWithReserveType(exposure, payableWithReserve);
  const { add } = useMinimizedDialogs();

  const {
    allowMultiplePendingPaymentRequests,
    allowIndemnityPaymentsOnlyForExistingPropertyOwner,
    isPaymentAllowedOnClosedExposureForPayableType,
    isExpensePaymentAllowedWithCoverageIssue,
    displayNetAmounts,
  } = usePaymentsConfiguration();

  if (exposure.is_coverage_issue_exists) {
    const isExemptFromCoverageIssueLock = payableType === 'expenses' && isExpensePaymentAllowedWithCoverageIssue;
    if (!isExemptFromCoverageIssueLock) {
      return <ExposureTooltipedLock exposure={exposure} />;
    }
  }

  // Assuming that for involved person claimant always exists
  if (
    allowIndemnityPaymentsOnlyForExistingPropertyOwner &&
    payableType === 'indemnity' &&
    exposure.involved_property &&
    !exposure.involved_property.owner_contact_id
  ) {
    return <TooltipLock title="Missing owner details" />;
  }

  const {
    paid_sum,
    pending_sum,
    vat_paid_sum,
    vat_pending_sum,
    net_paid_sum,
    net_pending_sum,
    are_pending_payment_requests_exist,
    payment_request_pending,
    are_pending_reserve_change_requests_exist,
  } = payableWithReserve;

  const allowIfDenied = payableType === 'expenses' || claim.type === 'wc_claim';

  const allowIfClosed = isPaymentAllowedOnClosedExposureForPayableType(payableType);

  const requiresIncomeProtectionScreen =
    exposure.damage_assessment_type === 'income_protection' && payableType === 'indemnity';

  const disableChangePayment =
    (exposure.is_pending_request_exists && !are_pending_payment_requests_exist) ||
    requiresIncomeProtectionScreen ||
    isExposureWriteDisabled(exposure, user, {
      enableIfDenied: allowIfDenied,
      allowOnClosedExposure: allowIfClosed,
    });

  const disableMakePayment =
    are_pending_reserve_change_requests_exist ||
    are_pending_payment_requests_exist ||
    requiresIncomeProtectionScreen ||
    isExposureWriteDisabled(exposure, user, {
      enableIfDenied: allowIfDenied,
      allowOnClosedExposure: allowIfClosed,
    });

  // Gross figure: drives which payment dialog opens, so it must stay gross regardless of what is
  // displayed. The net figures below are display-only.
  const paidIncludingPending = paid_sum + pending_sum;

  const netPaidIncludingPending = net_paid_sum + net_pending_sum;
  const vatIncludingPending = vat_paid_sum + vat_pending_sum;

  const shouldDisplaySinglePaymentRequest = !allowMultiplePendingPaymentRequests || paidIncludingPending === 0;

  const commonDialogProps = {
    claim,
    exposure,
    payableWithReserve,
    payableType,
    onUpdate: onClaimUpdate,
  };

  const singlePaymentRequestDialogProps = {
    ...commonDialogProps,
    paymentRequest: payment_request_pending,
    cardDialogProps: {
      isDialog: true,
      maxWidth: 'md',
      fullWidth: true,
    },
    refreshInBackground: true,
  };

  const multiPaymentsDialogProps = {
    ...commonDialogProps,
  };

  const openPaymentRequestDialog = () => {
    const { handleCloseDialog } = add({
      barHeader: 'New Payment Request',
      type: 'PAYMENT',
      dialogComponent: shouldDisplaySinglePaymentRequest ? MinimizedPaymentRequestContainer : MultiPaymentsDialog,
      dialogProps: {
        ...(shouldDisplaySinglePaymentRequest ? singlePaymentRequestDialogProps : multiPaymentsDialogProps),
        onClose: () => handleCloseDialog(),
      },
    });
  };

  const handleClick = () => {
    openPaymentRequestDialog();

    if (!payment_request_pending && shouldDisplaySinglePaymentRequest) {
      mixpanel.track(PAYMENT_MIXPANEL_EVENTS.SET_PAYMENT_CLICKED);
    }
  };

  const tooltipTitle = requiresIncomeProtectionScreen
    ? 'Payments can be made only via the DA Income protection screen'
    : null;

  const sumDisplay = (
    <SumContainer
      sum={displayNetAmounts ? netPaidIncludingPending : paidIncludingPending}
      color={are_pending_payment_requests_exist ? PENDING_COLOR : undefined}
      fontWeight={exposure?.isGeneralExpenses ? 700 : undefined}
    />
  );

  // TODO: refactor - after we will refactor Reserves table we may add PaidSumContainer in the proper cell
  return (
    <>
      <HoverActionField
        onAction={handleClick}
        icon={disableMakePayment ? PencilIcon : CreditCardIcon}
        disabled={disableMakePayment && disableChangePayment}
        permanent
        actionAriaLabel={`Set ${payableType} payment`}
        tooltipTitle={tooltipTitle}
      >
        {displayNetAmounts ? (
          <GrossVatTooltip grossAmount={paidIncludingPending} vatAmount={vatIncludingPending}>
            {sumDisplay}
          </GrossVatTooltip>
        ) : (
          sumDisplay
        )}
      </HoverActionField>
    </>
  );
}

PaidSumContainer.propTypes = {
  payableWithReserve: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
};

export default PaidSumContainer;
